import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

interface InstagramAccount {
  id: string;
  username: string;
  name?: string;
  profile_picture_url?: string;
  followers_count?: number;
  follows_count?: number;
  media_count?: number;
}

interface IGPage {
  pageId: string;
  pageName: string;
  igBusinessId: string;
  instagramAccount: InstagramAccount;
}

interface SessionData {
  storeId: string;
  pages: { id: string; name: string }[];
  instagramAccounts: IGPage[];
}

export default function InstagramConnectPage() {
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const sessionId = query.get('session'); // ✅ get from query param

  const [data, setData] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      console.error('❌ Missing sessionId in query');
      setError('Missing sessionId in URL');
      setLoading(false);
      return;
    }

    const fetchSession = async () => {
      try {
        const res = await fetch(
          `https://api.qmagacin.store/api/ig/session/${sessionId}`
        );

        if (!res.ok) {
          const text = await res.text();
          console.error('❌ Failed fetching session:', res.status, text);
          throw new Error(`Failed to fetch session: ${res.status}`);
        }

        const json: SessionData = await res.json();

        setData(json);
      } catch (err) {
        console.error('❌ Error fetching session:', err);
        setError('Failed to load session data');
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [sessionId]);

  const handleConnect = async (pageId: string) => {
    if (!data || !sessionId) return;

    try {
      const res = await fetch('https://api.qmagacin.store/api/ig/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, pageId }),
      });

      const json = await res.json();

      if (res.ok && json.success) {
        alert('Instagram connected successfully!');
        navigate('/stores'); // redirect after connect
      } else {
        alert('Failed to connect Instagram account');
      }
    } catch (err) {
      console.error('❌ Connect error:', err);
      alert('Error connecting Instagram account');
    }
  };

  if (loading) return <div>Loading Instagram accounts...</div>;
  if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;
  if (!data) return <div>No session data found</div>;

  const accounts = data.instagramAccounts;

  return (
    <div style={{ padding: '2rem' }}>
      <h2>Choose a Page / Instagram account to connect</h2>

      {accounts.length === 0 && <p>No Instagram Business accounts found.</p>}

      <ul style={{ listStyle: 'none', padding: 0 }}>
        {accounts.map(a => {
          const ig = a.instagramAccount;
          return (
            <li
              key={a.pageId}
              style={{
                border: '1px solid #ddd',
                borderRadius: 8,
                padding: 16,
                marginBottom: 12,
              }}
            >
              <strong>{a.pageName}</strong> — @{ig.username}
              {ig.profile_picture_url && (
                <div>
                  <img
                    src={ig.profile_picture_url}
                    alt={ig.username}
                    width={48}
                    style={{ borderRadius: 6, marginTop: 8 }}
                  />
                </div>
              )}
              <div>Followers: {ig.followers_count}</div>
              <div>Following: {ig.follows_count}</div>
              <div>Posts: {ig.media_count}</div>
              <button
                style={{ marginTop: 8, padding: '4px 12px' }}
                onClick={() => handleConnect(a.pageId)}
              >
                Connect this account
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
